/**
 * BasemapToolCard — Card 1 from
 * `VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §7.1.
 *
 * Implements the full basemap surface (switch + on/off + opacity + style +
 * labels + brightness/contrast + coordinate readout + scale bar slot +
 * north arrow + reset/home + geocoder + screenshot/share). The card is
 * rendered by the right-rail `ToolCardDock` whenever
 * `useViewerStore.sceneSelection.toolCardKind === 'basemap'`.
 *
 * The card emits its mutations through `useViewerStore.setBasemapToolCard`
 * so adapter mocks and tests can observe a stable contract. The MapLibre
 * adapter side-effect (swapping tile sources) is delegated to the
 * existing `applyBasemap` helper when a container is provided — keeping
 * the engine binding optional so the card stays unit-testable without a
 * mounted map.
 *
 * Design-system compliance (WP-3):
 *   - Tokens only; no raw hex / rgba / pixel literals in this file.
 *   - Material Symbols rounded only (`map`, `image`, `tune`, `flare`,
 *     `contrast`, `north`, `restart_alt`, `screenshot_monitor`, `block`,
 *     `search`).
 *   - 40 px minimum touch target on every chip, slider thumb, and button.
 *   - One primary CTA per action row (the geocoder search lives in the
 *     coordinate-readout section; screenshot/share is a single CTA in
 *     the header slot).
 *   - Numeric readouts (opacity %, brightness ×, contrast ×, coordinate)
 *     in mono via `tokens.font.role.engMono`.
 */

import { useCallback, useMemo } from 'react';
import {
  BASEMAPS,
  applyBasemap,
  savePreferredBasemapId,
  type BasemapId,
  type BasemapDefinition,
} from '@/engines/maplibre/basemaps';
import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import {
  Banner,
  Chip,
  Icon,
  tokens,
  type ChipVariant,
} from '@/design';
import {
  ToolCardSection,
  ToolCardShell,
  ToolCardSliderRow,
  ToolCardToggleRow,
} from '@/components/toolcards/ToolCardShell';
import { useViewerStore } from '@/state/useViewerStore';
import { useViewerSlice } from '@/state/useViewerSlice';

export type BasemapTileHealth =
  | 'rest'
  | 'loading'
  | 'partial'
  | 'offline'
  | 'stale'
  | 'low-accuracy';

export interface BasemapCoordinate {
  readonly lon: number;
  readonly lat: number;
}

export interface BasemapToolCardProps {
  /** Map container; required to swap basemaps on the live MapLibre host. */
  container?: HTMLElement | null;
  /** Current cursor coordinate (lng/lat); rendered in mono in §7.1 row. */
  coordinate?: BasemapCoordinate | null;
  /** Map scale denominator (e.g. 250000 → "1:250 000"). */
  scaleDenominator?: number | null;
  /** Map bearing in degrees (0 = north). Drives the compass widget. */
  bearingDeg?: number | null;
  /** Tile health for the loading/stale/offline banner row. */
  tileHealth?: BasemapTileHealth;
  /** Optional reset-view callback (fit project bbox / home view). */
  onResetView?: () => void;
  /** Optional reset-to-north callback (zero bearing). */
  onResetNorth?: () => void;
  /** Optional geocoder open callback. */
  onOpenGeocoder?: () => void;
  /** Optional screenshot/share view callback. */
  onShareView?: () => void;
  /** Read-only / permission-denied banner content (string). */
  permissionDenied?: string | null;
  /** Override the "only render when basemap selected" gate (for testing). */
  forceVisible?: boolean;
  'data-testid'?: string;
}

const TILE_HEALTH_TO_CHIP: Record<
  BasemapTileHealth,
  { label: string; variant: ChipVariant; icon: 'sync_problem' | 'cloud_off' | 'wrong_location' | 'check_circle' | 'pending' }
> = {
  rest: { label: 'Tiles ready', variant: 'success', icon: 'check_circle' },
  loading: { label: 'Loading tiles', variant: 'info', icon: 'pending' },
  partial: { label: 'Partial tiles', variant: 'warning', icon: 'sync_problem' },
  offline: { label: 'Offline tiles', variant: 'warning', icon: 'cloud_off' },
  stale: { label: 'Stale tiles', variant: 'warning', icon: 'sync_problem' },
  'low-accuracy': {
    label: 'Low-accuracy basemap',
    variant: 'warning',
    icon: 'wrong_location',
  },
};

function formatOpacity(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatMultiplier(value: number): string {
  return `${value.toFixed(2)}×`;
}

function formatScale(denom: number | null | undefined): string {
  if (denom == null || !Number.isFinite(denom)) return '—';
  const rounded = Math.round(denom);
  return `1:${rounded.toLocaleString('en-US')}`;
}

function formatBearing(deg: number | null | undefined): string {
  if (deg == null || !Number.isFinite(deg)) return '—';
  const normalized = ((deg % 360) + 360) % 360;
  return `${normalized.toFixed(1)}°`;
}

function formatLngLat(p: BasemapCoordinate | null | undefined): string {
  if (!p) return '— , —';
  return `${p.lon.toFixed(6)}, ${p.lat.toFixed(6)}`;
}

export function BasemapToolCard({
  container,
  coordinate,
  scaleDenominator,
  bearingDeg,
  tileHealth = 'rest',
  onResetView,
  onResetNorth,
  onOpenGeocoder,
  onShareView,
  permissionDenied,
  forceVisible,
  'data-testid': testId,
}: BasemapToolCardProps) {
  const toolCardKind = useViewerSlice(
    (s) => s.sceneSelection.toolCardKind ?? 'gtwin',
  );
  const state = useViewerSlice((s) => s.basemapToolCard);
  const setBasemapToolCard = useViewerStore((s) => s.setBasemapToolCard);

  const active: BasemapDefinition = useMemo(
    () =>
      BASEMAPS.find((b) => b.id === state.activeBasemapId) ?? BASEMAPS[0],
    [state.activeBasemapId],
  );
  // Raster basemaps support brightness / contrast; the "none" entry does
  // not. Detect via the presence of any layer in the registry definition.
  const supportsRasterTone = active.layers.length > 0;

  const applyToMap = useCallback(
    (id: BasemapId) => {
      if (!container) return;
      const map = getMapLibreHostHandle(container);
      if (!map) return;
      try {
        applyBasemap(map, id);
      } catch (err) {
        console.warn('[BasemapToolCard] applyBasemap failed', err);
      }
    },
    [container],
  );

  const handleSelectBasemap = useCallback(
    (id: BasemapId) => {
      setBasemapToolCard({ activeBasemapId: id });
      savePreferredBasemapId(id);
      applyToMap(id);
    },
    [applyToMap, setBasemapToolCard],
  );

  const visible = forceVisible || toolCardKind === 'basemap';
  if (!visible) return null;

  const tileChip = TILE_HEALTH_TO_CHIP[tileHealth];

  return (
    <ToolCardShell
      icon="map"
      title="Basemap & imagery"
      subtitle={active.label}
      data-testid={testId ?? 'basemap-toolcard'}
      primaryAction={
        onShareView ? (
          <button
            type="button"
            aria-label="Share or screenshot map view"
            onClick={onShareView}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.accent.primary,
              color: tokens.color.accent.on,
              border: `${tokens.border.width.thin}px solid transparent`,
              borderRadius: tokens.radius.input,
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              fontWeight: tokens.font.weight.medium,
              cursor: 'pointer',
            }}
          >
            <Icon name="screenshot_monitor" size={20} decorative />
            Share
          </button>
        ) : null
      }
      topBanners={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[2],
          }}
        >
          {permissionDenied ? (
            <Banner
              severity="warning"
              title="Basemap restricted"
              body={permissionDenied}
              narrationKey="basemap-toolcard-permission"
            />
          ) : null}
          {tileHealth !== 'rest' ? (
            <Chip
              icon={tileChip.icon}
              variant={tileChip.variant}
              label={tileChip.label}
            />
          ) : null}
        </div>
      }
    >
      <ToolCardSection title="Imagery">
        <div
          role="radiogroup"
          aria-label="Basemap"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: tokens.space[1],
          }}
        >
          {BASEMAPS.map((b) => {
            const isActive = b.id === state.activeBasemapId;
            return (
              <button
                key={b.id}
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => handleSelectBasemap(b.id)}
                data-testid={`basemap-option-${b.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: tokens.space[2],
                  padding: tokens.space[2],
                  minHeight: tokens.density.touchMin,
                  background: isActive
                    ? tokens.color.accent.subtle
                    : tokens.color.surface[1],
                  color: tokens.color.ink.primary,
                  border: isActive
                    ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
                    : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                  borderRadius: tokens.radius.input,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.size.sm,
                }}
              >
                <Icon name={b.thumbnailIcon} size={24} decorative />
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                  }}
                >
                  <span style={{ fontWeight: tokens.font.weight.medium }}>
                    {b.label}
                  </span>
                  <span
                    style={{
                      fontSize: tokens.font.size.xs,
                      color: tokens.color.ink.secondary,
                    }}
                  >
                    {b.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </ToolCardSection>

      <ToolCardSection title="Visibility">
        <ToolCardToggleRow
          label="Basemap visible"
          description="Show the basemap behind your data layers."
          icon="visibility"
          checked={state.visible}
          onChange={(next) => setBasemapToolCard({ visible: next })}
          data-testid="basemap-visible-toggle"
        />
        <ToolCardSliderRow
          label="Opacity"
          icon="tune"
          min={0}
          max={1}
          step={0.05}
          value={state.opacity}
          readout={formatOpacity(state.opacity)}
          onChange={(next) => setBasemapToolCard({ opacity: next })}
          data-testid="basemap-opacity-slider"
        />
        <ToolCardToggleRow
          label="Labels"
          icon="label"
          checked={state.labelsOn}
          onChange={(next) => setBasemapToolCard({ labelsOn: next })}
          data-testid="basemap-labels-toggle"
        />
      </ToolCardSection>

      {supportsRasterTone ? (
        <ToolCardSection
          title="Raster tone"
          description="Brightness / contrast only apply to raster basemaps."
        >
          <ToolCardSliderRow
            label="Brightness"
            icon="flare"
            min={0.5}
            max={1.5}
            step={0.05}
            value={state.brightness}
            readout={formatMultiplier(state.brightness)}
            onChange={(next) => setBasemapToolCard({ brightness: next })}
            data-testid="basemap-brightness-slider"
          />
          <ToolCardSliderRow
            label="Contrast"
            icon="contrast"
            min={0.5}
            max={1.5}
            step={0.05}
            value={state.contrast}
            readout={formatMultiplier(state.contrast)}
            onChange={(next) => setBasemapToolCard({ contrast: next })}
            data-testid="basemap-contrast-slider"
          />
        </ToolCardSection>
      ) : (
        <ToolCardSection title="Raster tone">
          <Chip
            icon="block"
            variant="neutral"
            label="No raster basemap selected"
          />
        </ToolCardSection>
      )}

      <ToolCardSection title="Coordinate & scale">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: tokens.space[2],
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Lng / Lat
            </div>
            <div>{formatLngLat(coordinate)}</div>
          </div>
          <div>
            <div
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Scale
            </div>
            <div>{formatScale(scaleDenominator)}</div>
          </div>
          <div>
            <div
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Bearing
            </div>
            <div>{formatBearing(bearingDeg)}</div>
          </div>
        </div>
      </ToolCardSection>

      <ToolCardSection title="Navigation">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[2],
          }}
        >
          <button
            type="button"
            onClick={onResetView}
            disabled={!onResetView}
            data-testid="basemap-reset-view"
            aria-label="Reset to project home view"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor: onResetView ? 'pointer' : 'default',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              opacity: onResetView ? 1 : 0.5,
            }}
          >
            <Icon name="restart_alt" size={20} decorative />
            Home view
          </button>
          <button
            type="button"
            onClick={onResetNorth}
            disabled={!onResetNorth}
            data-testid="basemap-reset-north"
            aria-label="Reset bearing to north"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor: onResetNorth ? 'pointer' : 'default',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              opacity: onResetNorth ? 1 : 0.5,
            }}
          >
            <Icon name="north" size={20} decorative />
            Reset north
          </button>
          <button
            type="button"
            onClick={onOpenGeocoder}
            disabled={!onOpenGeocoder}
            data-testid="basemap-open-geocoder"
            aria-label="Open geocoder search"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor: onOpenGeocoder ? 'pointer' : 'default',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              opacity: onOpenGeocoder ? 1 : 0.5,
            }}
          >
            <Icon name="search" size={20} decorative />
            Geocoder
          </button>
        </div>
      </ToolCardSection>
    </ToolCardShell>
  );
}
